import * as React from 'react';
import { useDebounce } from 'use-debounce';

import { Endpoint, SearchResult } from '../types';

/**
 * Filters endpoints that have a matching summary or description with the search query.
 */
export function useFilterEndpoints({ search, allEndpoints }: { search: string; allEndpoints: Endpoint[] }) {
  const [debouncedSearch] = useDebounce(search, 300);
  const [data, setData] = React.useState<SearchResult[]>([]);
  const [isFetching, setIsFetching] = React.useState(false);
  const MAX_DESCRIPTION_LENGTH = 250;

  React.useEffect(() => {
    setIsFetching(true);
    const searchRegex = new RegExp(`(${debouncedSearch})`, 'gi');

    const highlightMatch = (text: string): string | null => {
      if (!text) return null;
      const truncated = text.slice(0, MAX_DESCRIPTION_LENGTH);
      const suffix = text.length >= MAX_DESCRIPTION_LENGTH ? '...' : '';
      return truncated.replace(searchRegex, match => `<mark>${match}</mark>`) + suffix;
    };

    const searchResults: SearchResult[] = allEndpoints
      .filter(endpoint => searchRegex.test(endpoint.summary) || searchRegex.test(endpoint.description))
      .map(endpoint => ({
        ...endpoint,
        highlighted: {
          summary: highlightMatch(endpoint.summary),
          description: highlightMatch(endpoint.description),
        },
      }));
    setData(searchResults);
    setIsFetching(false);
  }, [debouncedSearch, allEndpoints]);

  return { data, isFetching };
}
